import React, { useState } from 'react';
import { FiExternalLink, FiBookOpen, FiAward, FiSearch } from 'react-icons/fi';
import './SampleLibrary.css';

const CURATED_SAMPLES = [
  {
    category: 'NIH',
    items: [
      {
        title: 'NIAID Sample Applications (R01, K08, R21)',
        description: 'Annotated sample applications from the National Institute of Allergy and Infectious Diseases, including reviewer critiques.',
        url: 'https://www.niaid.nih.gov/grants-contracts/sample-applications',
        type: 'funded',
        mechanism: 'R01, K08, R21',
      },
      {
        title: 'NIH Application Advice by Section',
        description: 'Official NIH guidance on writing each section: Specific Aims, Significance, Innovation, Approach.',
        url: 'https://grants.nih.gov/grants-process/write-application/advice-on-application-sections',
        type: 'guide',
        mechanism: 'All',
      },
      {
        title: 'NIGMS Sample R01 Applications',
        description: 'Sample funded R01 applications from the National Institute of General Medical Sciences.',
        url: 'https://www.nigms.nih.gov/training/strategicplanimplementationblueprint/pages/SampleApplications.aspx',
        type: 'funded',
        mechanism: 'R01',
      },
      {
        title: 'NCI Sample Grant Applications',
        description: 'Sample applications from the National Cancer Institute across multiple mechanisms.',
        url: 'https://www.cancer.gov/grants-training/grants-funding/funding-opportunities/sample-grant-applications',
        type: 'funded',
        mechanism: 'R01, U01, P01',
      },
    ],
  },
  {
    category: 'NSF',
    items: [
      {
        title: 'NSF BCS Sample Proposal',
        description: 'Complete sample proposal from the Division of Behavioral and Cognitive Sciences.',
        url: 'https://www.nsf.gov/sbe/bcs/sample-proposal',
        type: 'funded',
        mechanism: 'Standard',
      },
      {
        title: 'Successful NSF Proposals Collection (Carleton)',
        description: 'Curated collection of successful NSF proposals in geoscience and STEM education with commentary.',
        url: 'https://serc.carleton.edu/NAGTWorkshops/earlycareer/research/NSFgrants.html',
        type: 'funded',
        mechanism: 'Various',
      },
      {
        title: 'NSF Proposal Preparation Guide (PAPPG)',
        description: 'Official NSF Proposal & Award Policies and Procedures Guide — the definitive reference.',
        url: 'https://www.nsf.gov/funding/preparing-proposal',
        type: 'guide',
        mechanism: 'All',
      },
    ],
  },
  {
    category: 'University Collections',
    items: [
      {
        title: 'UChicago Sample Proposals',
        description: 'Collection of funded proposals across NIH, NSF, DOE, and private foundations from University of Chicago researchers.',
        url: 'https://ura.uchicago.edu/resource-library/sample-proposals',
        type: 'funded',
        mechanism: 'Various',
      },
      {
        title: 'UAF Sample Funded Proposals',
        description: 'Funded proposals from University of Alaska Fairbanks across multiple agencies.',
        url: 'https://www.uaf.edu/ogca/lifecycle/3-develop/sample-funded-proposals.php',
        type: 'funded',
        mechanism: 'Various',
      },
      {
        title: 'USF Proposal Samples',
        description: 'Sample proposals and tools from University of South Florida College of Arts and Sciences.',
        url: 'https://www.usf.edu/arts-sciences/research-scholarship/proposal-tools/proposal-samples.aspx',
        type: 'funded',
        mechanism: 'Various',
      },
      {
        title: 'Northwestern Annotated Grant Proposals',
        description: 'Annotated sample grant proposals for undergraduate and graduate researchers.',
        url: 'https://undergradresearch.northwestern.edu/advising/sample-grant-proposals/',
        type: 'funded',
        mechanism: 'Various',
      },
    ],
  },
  {
    category: 'Writing Guides',
    items: [
      {
        title: 'Harvard — Essentials for Writing a Winning Proposal',
        description: 'Comprehensive guide from Harvard Medical School on grant writing strategy and structure.',
        url: 'https://learn.hms.harvard.edu/insights/all-insights/essentials-writing-winning-grant-proposal',
        type: 'guide',
        mechanism: 'NIH',
      },
      {
        title: 'NSF Merit Review Process',
        description: 'How NSF makes funding decisions — Intellectual Merit and Broader Impacts criteria explained.',
        url: 'https://www.nsf.gov/funding/merit-review',
        type: 'guide',
        mechanism: 'NSF',
      },
      {
        title: 'NIH Reviewer Guidance',
        description: 'How NIH reviewers score applications — understand the 1-9 scoring system and what reviewers look for.',
        url: 'https://grants.nih.gov/grants/peer/reviewer_guidelines.htm',
        type: 'guide',
        mechanism: 'NIH',
      },
    ],
  },
];

const TYPE_BADGE = {
  funded: { label: 'Funded Example', cls: 'sl-badge--funded' },
  rejected: { label: 'Rejected Example', cls: 'sl-badge--rejected' },
  guide: { label: 'Writing Guide', cls: 'sl-badge--guide' },
};

const SampleLibrary = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  const filtered = CURATED_SAMPLES.map(cat => ({
    ...cat,
    items: cat.items.filter(item => {
      const matchesSearch = !searchTerm ||
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.mechanism || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !activeCategory || cat.category === activeCategory;
      return matchesSearch && matchesCategory;
    }),
  })).filter(cat => cat.items.length > 0);

  return (
    <div className="sl-page">
      <div className="sl-header">
        <div>
          <h2><FiBookOpen /> Sample Proposals Library</h2>
          <p className="sl-subtitle">
            Publicly available funded proposals and writing guides from NIH, NSF, and universities.
            Study these to understand what gets funded before you draft your own.
          </p>
        </div>
      </div>

      <div className="sl-controls">
        <div className="sl-search">
          <FiSearch />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by agency, mechanism, or keyword..."
          />
        </div>
        <div className="sl-filters">
          <button className={`sl-filter ${!activeCategory ? 'sl-filter--active' : ''}`} onClick={() => setActiveCategory(null)}>All</button>
          {CURATED_SAMPLES.map(cat => (
            <button key={cat.category} className={`sl-filter ${activeCategory === cat.category ? 'sl-filter--active' : ''}`}
              onClick={() => setActiveCategory(cat.category === activeCategory ? null : cat.category)}>
              {cat.category}
            </button>
          ))}
        </div>
      </div>

      {filtered.map(cat => (
        <div key={cat.category} className="sl-category">
          <h3>{cat.category}</h3>
          <div className="sl-grid">
            {cat.items.map((item, i) => {
              const badge = TYPE_BADGE[item.type] || TYPE_BADGE.guide;
              return (
                <a key={i} href={item.url} target="_blank" rel="noreferrer" className="sl-card">
                  <div className="sl-card-top">
                    <span className={`sl-badge ${badge.cls}`}>{badge.label}</span>
                    {item.mechanism && <span className="sl-mechanism">{item.mechanism}</span>}
                    <FiExternalLink className="sl-link-icon" />
                  </div>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </a>
              );
            })}
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <div className="sl-empty">No samples match your search.</div>
      )}
    </div>
  );
};

export default SampleLibrary;
